<template>
    <md-toolbar class="md-dense ql-cue-header" ref="ql-cue-header" v-if="cue && cue.data">
        <div
            class="ql-cue-layer-func"
        >
            <div
                class="ql-cue-layer-func-back"
                @click="toggle"
                @contextmenu="preventContextFunc($event)"
                :style="{backgroundColor: layer.color, height: (this.titleFontSize + 1) + 'px'}"
            ></div>
        </div>
        <div class="cue-header-title" :class="{'cue-header-both': [headerBox.title, headerBox.label, headerBox.time].filter(Boolean).length >= 2}" :id="'cue-header-title-' + cue.id" :style="cueHeaderTitleStyles">
            <layer-picker
                :currentLayer="layer"
                :layers="layers"
                :editable="editable"
                @selectLayer="selectLayer"
                :locked="locked"
                :totalPages="totalPages"
                :cue="cue"
                :titleFontSize="titleFontSize"
                :layerCueLineOpacity="cueLineOpacity"
                :layerCueBoxOpacity="cueBoxOpacity"
                :headerBox="headerBox"
                :selectedScale="selectedScale"
            />
            <div class="cue-header-separator" v-if="showTitle || showLabel || showTime"></div>

            <div class="cue-header-cue-title" v-if="showTitle" @click="edit" :class="{'placeholder': !cue.title, 'short-box': (cue.title || '').length <= 12}" :style="titleStyle">
                {{cue.title || 'Cue Number'}}
                <md-tooltip md-direction="top" v-if="editable && !locked && !isEditing && !isEditingDescription">Click to edit</md-tooltip>
            </div>
            <div class="cue-header-separator" v-if="showTitle && showLabel"></div>
            <div class="cue-header-cue-description" v-if="showLabel" @click="editDescription" :class="{'placeholder': !cueLabel, 'short-box': cueLabel.length <= 12}" :style="titleStyle">
                {{cueLabel || 'Cue Label'}}
                <md-tooltip md-direction="top" v-if="editable && !locked && !isEditing && !isEditingDescription">Click to edit</md-tooltip>
            </div>
            <div class="cue-header-separator" v-if="(showTitle || showLabel) && showTime"></div>
            <div class="cue-header-cue-time" v-if="showTime" :class="{'placeholder': !cue.data.time, 'short-box': (cue.data.time || '').length <= 12}" :style="titleStyle">
                {{cue.data.time || 'Cue Time'}}
            </div>

        </div>

        <div class="button-container" :style="tetherWrapperStyle">
            <span class="move-button drag">
                <div class="fill-button" :style="tetherStyle"></div>
            </span>
        </div>
    </md-toolbar>
</template>

<script>
import { mapGetters } from 'vuex';
import { cueLabelOf } from '@/lib/cue-fields';

import ProjectCuesLayerPicker from '@/components/print/cues/layer-picker';

/**
 * Component For...
 */
const ProjectCuesCueHeader = {
    name: 'projectCuesCueHeader',
    props: [
        'cue',
        'expanded',
        'layer',
        'layers',
        'locked',
        'editable',
        'color',
        'totalPages',
        'selectedScale',
        'isEditing',
        'isEditingDescription',
        'isFloatingCue'
    ],
    data: function() {
        return {
            oldVal: this.$props.cue.title,
            oldDescriptionVal: cueLabelOf(this.$props.cue),
            updateCueTimerId: null
        };
    },
    computed: {
        // Old cues can lack data.label or the whole data map (21 Oct 2026).
        cueLabel() {
            return String(cueLabelOf(this.cue));
        },
        ...mapGetters([
            'userProjectData',
            'headerBoxOptions',
            'titleFontSizeOptions',
            'cueLineOpacityOptions',
            'cueBoxOpacityOptions',
            'getDefaultCuePlacementPrint',
            'layerByCue',
            'getCueLineOpacity',
            'getLayerBoxOpacity'
        ]),
        headerBox() {
            const layers = this.userProjectData.layers;
            let layerDefault;
            if (layers && layers[this.layer.id] && layers[this.layer.id].headerBox) {
                layerDefault = { ...layers[this.layer.id].headerBox };
            } else if (this.layer.headerBox) {
                layerDefault = { ...this.layer.headerBox };
            } else {
                layerDefault = {
                    title: true,
                    label: false,
                    time: false
                };
            }
            // Legacy layers/cues stored a headerBox without `layerName`; a missing value
            // means "shown", so this feature is off by default for existing data.
            if (layerDefault.layerName === undefined) {
                layerDefault.layerName = true;
            }
            // A per-cue override wins only for the keys it explicitly contains; any key it
            // omits (e.g. `time` on cues created before Time existed) falls back to the layer's
            // current global default, so later global setting changes still reach existing cues.
            if (this.headerBoxOptions[this.cue.id]) {
                return { ...layerDefault, ...this.headerBoxOptions[this.cue.id] };
            }
            return layerDefault;
        },
        // In the (non-editable) print view, hide a header field entirely when the
        // cue has no value for it, instead of printing an empty "Cue Number" /
        // "Cue Label" / "Cue Time" placeholder. When editable, keep showing it so
        // the field stays clickable to fill in.
        showTitle() {
            return this.headerBox.title && (!!this.cue.title || this.editable);
        },
        showLabel() {
            return this.headerBox.label && (!!(this.cue.data && this.cue.data.label) || this.editable);
        },
        showTime() {
            return this.headerBox.time && (!!(this.cue.data && this.cue.data.time) || this.editable);
        },
        titleFontSize() {
            if (this.titleFontSizeOptions[this.cue.id]) {
                return this.titleFontSizeOptions[this.cue.id];
            }
            if (this.cue.titleFontSize) {
                return this.cue.titleFontSize;
            }
            const layers = this.userProjectData.layers;
            if (layers && layers[this.layer.id]) {
                if (layers[this.layer.id].titleFontSize) {
                    return layers[this.layer.id].titleFontSize;
                }
            }
            return this.layer.titleFontSize || 14;
        },
        cueLineOpacity() {
            if (this.cueLineOpacityOptions[this.cue.id]) {
                return this.cueLineOpacityOptions[this.cue.id];
            }
            if (this.cue.lineOpacity) {
                return this.cue.lineOpacity;
            }
            return this.getCueLineOpacity(this.cue);
        },
        cueBoxOpacity() {
            if (this.cueBoxOpacityOptions[this.cue.id]) {
                return this.cueBoxOpacityOptions[this.cue.id];
            }
            if (this.cue.boxOpacity) {
                return this.cue.boxOpacity;
            }
            return this.getLayerBoxOpacity(this.cue);
        },
        titleStyle() {
            return {
                fontSize: this.titleFontSize + 'px'
            };
        },
        descriptionStyle() {
            return {
                fontSize: this.titleFontSize + 'px'
            };
        },
        tetherWrapperStyle() {
            return {
                width: `${10}px !important`,
                height: `${0}px !important`,
                backgroundColor: this.isFloatingCue ? this.layer.color : '#CCCCCC'
            };
        },
        tetherStyle() {
            const borderWidth = 16 - (((24 - this.titleFontSize) * 5) / 16);
            return {
                borderColor: this.isFloatingCue ? this.layer.color : '#CCCCCC',
                borderWidth: borderWidth + 'px'
            };
        },
        cueHeaderTitleStyles() {
            const marginRight = 12 - (((24 - this.titleFontSize) * 6) / 16);
            const styles = {
                marginRight: marginRight + 'px',
                maxWidth: `calc(100% - ${marginRight}px)`
            };
            // No note marker in print (Josh, 29 Aug 2026), so nothing reserves
            // width here — a cue with a note prints exactly as wide as one without.
            return styles;
        }
    },
    methods: {
        toggle() {
            this.$emit('toggle');
        },
        selectLayer(layer) {
            if (this.locked) {
                return;
            }
            this.$props.cue.layer = layer;
            this.$store.dispatch('updateCue', this.$props.cue);
        },
        edit() {
            if (this.locked || !this.editable) {
                return;
            }
            if (this.isEditing || this.isEditingDescription) return;
            if (!this.headerBox.title) {
                this.editDescription();
                return;
            }
            this.$emit('updateStatus', {
                isEditing: true
            });
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$refs.input_cue_title.$el.focus();
                }, 100);
            });
        },
        editDescription(e) {
            if (this.locked || !this.editable) {
                return;
            }
            if (e) {
                e.stopPropagation();
            }
            this.$emit('updateStatus', {
                isEditingDescription: true
            });
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$refs.input_cue_description.$el.focus();
                }, 100);
            });
        },
        keyPress(e) {
            if (e.keyCode === 27) {
                this.$emit('updateStatus', {
                    isEditing: false
                });
                this.$props.cue.title = this.oldVal;
            }
        },
        keyDown(e) {
            if (e.key === 'Tab' && this.headerBox.title && this.headerBox.label) {
                e.preventDefault();
                this.modifyCue('title', e.target.value).then(() => {
                    this.editDescription();
                });
            }
            if (e.key === 'Enter') {
                this.modifyCue('title', e.target.value);
                e.preventDefault();
            }
            if (e.key === 'Escape') {
                this.$emit('updateStatus', {
                    isEditing: false
                });
                this.$refs.input_cue_title.$el.value = this.oldVal;
                this.$props.cue.title = this.oldVal;
                this.cue.title = this.oldVal;
            }
        },
        keyPressDescription(e) {
            if (e.keyCode === 27) {
                this.$emit('updateStatus', {
                    isEditingDescription: false
                });
                this.$props.cue.data.label = this.oldDescriptionVal;
            }
        },
        keyDownDescription(e) {
            if (e.key === 'Tab' && this.headerBox.title && this.headerBox.label) {
                e.preventDefault();
                this.modifyCue('data.label', e.target.value).then(() => {
                    this.edit();
                });
            }
            if (e.key === 'Enter') {
                this.modifyCue('data.label', e.target.value);
                e.preventDefault();
            }
            if (e.key === 'Escape') {
                this.$emit('updateStatus', {
                    isEditingDescription: false
                });
                this.$refs.input_cue_description.$el.value = this.oldDescriptionVal;
                this.$props.cue.data.label = this.oldDescriptionVal;
                this.cue.data.label = this.oldDescriptionVal;
            }
        },
        submit() {
            this.$emit('updateStatus', {
                isEditing: false
            });
        },
        submitDescription() {
            this.$emit('updateStatus', {
                isEditingDescription: false
            });
        },
        updateCue(cue) {
            this.$store.dispatch('updateCue', cue);
        },
        updateCueTitle() {
            this.$emit('updateStatus', {
                isEditing: false
            });
            this.oldVal = this.$props.cue.title;
            this.$store.dispatch('updateCue', this.cue);
        },
        updateCueDescription() {
            this.$emit('updateStatus', {
                isEditingDescription: false
            });
            this.oldDescriptionVal = this.$props.cue.data.label;
            this.$store.dispatch('updateCue', this.cue);
        },
        modifyCue(key, value) {
            return new Promise((resolve, reject) => {
                if (this.updateCueTimerId) {
                    clearTimeout(this.updateCueTimerId);
                }
                this.updateCueTimerId = setTimeout(() => {
                    if (value !== undefined) {
                        window.objectPath.set(this.cue, key, value);
                    }
                    this.$emit('updateStatus', {
                        isEditing: false,
                        isEditingDescription: false
                    });
                    this.oldVal = this.$props.cue.title;
                    this.oldDescriptionVal = this.$props.cue.data.label;

                    this.$store.dispatch('modifyCue', {
                        id: this.cue.id,
                        data: {
                            [key]: value || window.objectPath.get(this.cue, key)
                        }
                    }).then(() => {
                        resolve();
                    }).catch(() => {
                        reject();
                    });
                }, 10);
            });
        },
        untetherCue() {
            if (this.editable) {
                let cueTopPos = 0;
                const header = this.$refs['ql-cue-header'].$el;
                const parent = header.closest('.ql-cue');
                if (parent) {
                    cueTopPos = window.getComputedStyle(parent).getPropertyValue('top');
                }
                cueTopPos = cueTopPos.replaceAll('px', '');

                const defaultCuePlacement = this.getDefaultCuePlacementPrint(this.cue.layer);
                
                let unTetherLeft = 0;
                if (defaultCuePlacement === 'left') {
                    unTetherLeft = `${2 * this.selectedScale}`;
                }
                if (defaultCuePlacement === 'right') {
                    unTetherLeft = `${(this.svgContentWidth / this.selectedScale) - 100}`;
                }

                this.$store.dispatch('addFloatingCue', {
                    id: this.cue.id,
                    left: Math.max(unTetherLeft, 0),
                    top: Math.max(cueTopPos, 0)
                });
                this.$emit('untetherCue');
            }
        },
        preventContext(e) {
            this.$store.dispatch('removeCueFloating', {
                id: this.cue.id
            });
            this.$emit('tetherCue');
            e.preventDefault();
            e.stopPropagation();
            return false;
        },
        preventContextFunc: function(e) {
            e.preventDefault();
            e.stopPropagation();
            if (!this.editable) return;
            this.cue.data.workNote = ' ';
            this.$store.dispatch('updateCue', this.cue);
        }
    },
    components: {
        'layer-picker': ProjectCuesLayerPicker
    }
};

export default ProjectCuesCueHeader;
</script>

<style lang="scss">
.app-print-mode {
    .ql-cue-header {
        &.md-toolbar {
            min-height: 0px;
            overflow: hidden;
        }
        padding: 0;
        flex-wrap: nowrap;
        
        .ql-cue-layer-func {
            cursor: pointer;
            width: 10px;
            min-width: 10px;
            position: relative;
            .ql-cue-layer-func-back {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                left: 0;
                width: 60%;
            }
        }

        .cue-header-title {
            position: relative;
            margin: 0 12px 0 0;
            flex: 1;
            max-width: calc(100% - 28px);
            font-size: 14px;
            font-weight: 400;
            display: flex;
            align-items: stretch;
            line-height: 1.1;

            &.cue-header-both {
                .cue-header-cue-title {
                    flex: unset;
                    &.short-box {
                        min-width: min-content;
                    }
                }
                .cue-header-cue-description {
                    flex: unset;
                    &.short-box {
                        min-width: min-content;
                    }
                }
                .cue-header-cue-time {
                    flex: unset;
                    &.short-box {
                        min-width: min-content;
                    }
                }
            }

            .cue-header-layer-title {
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                flex: 0 0 auto;
            }
            .cue-header-separator {
                align-self: stretch;
                flex: 0 0 1px;
                width: 1px;
                background: #aaa;
                margin: 0 8px;
            }
            .cue-header-cue-title {
                flex: 1 0 0;
                position: relative;
                font-weight: 100;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                &.placeholder {
                    font-size: 16px;
                    color: #999;
                }
                // eslint-disable-next-line
                min-width: calc(min(40px, 10px));
            }
            .cue-header-cue-description {
                flex: 1 0 0;
                position: relative;
                font-weight: 100;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                z-index: 2;
                &.placeholder {
                    font-size: 16px;
                    color: #999;
                }
                // eslint-disable-next-line
                min-width: calc(min(40px, 10px));
            }
            .cue-header-cue-time {
                flex: 1 0 0;
                position: relative;
                font-weight: 100;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                z-index: 2;
                &.placeholder {
                    font-size: 16px;
                    color: #999;
                }
                // eslint-disable-next-line
                min-width: calc(min(40px, 10px));
            }
            .md-input {
                height: 22px;
                line-height: 22px;
            }
        }
        > .md-icon-button {
            width: 32px;
            height: 32px;
            margin: 0;
            min-width: 32px;
        }
        .cue-header-title-edit, .cue-header-description-edit {
            font-size: 14px;
            max-height: 22px;
            min-height: 22px !important;
        }

        .button-container {
            width: 16px;
            height: 16px;
            overflow: hidden;
            display: flex;
            cursor: pointer;
            position: absolute;
            right: 0px;
            top: 0px;
            transform: translate(50%, -50%) rotate(45deg);
            .move-button {
                display: block;
                text-align: center;
                .fill-button {
                    width: 0;
                    height: 0;
                    border: solid 8px transparent;
                }
            }
        }
    }

    .md-theme-default.md-toolbar.ql-cue-header {
        background-color: white;
        color: black;
        .cue-header-title {
            background-color: white;
            color: black;
            input {
                font-size: 14px;
                font-weight: 100;
                background-color: white !important;
                color: black !important;
                -webkit-text-fill-color: black !important;
                &::-webkit-input-placeholder {
                    color: #ccc;
                }
            }
        }
    }

    .md-theme-show-mode.md-toolbar.ql-cue-header {
        background-color: #424242;
        color: white;
        .cue-header-title {
            background-color: #424242;
            color: white;
            input {
                font-size: 14px;
                font-weight: 100;
                background-color: #424242 !important;
                color: white !important;
                -webkit-text-fill-color: white !important;
            }
            .md-input-focused:after {
                background-color: rgba(255, 255, 255, 0.6) !important;
            }
            .cue-header-separator {
                background: #999;
            }
        }
    }
}
</style>


// WEBPACK FOOTER //
// cue-header.vue?30cad938